import { HostType } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Box, Button, Input, Modal, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-react';
import { useState } from 'react';

import { constants } from '../../../common/Localization';
import { deepCopy } from '../../../common/util';

interface Props {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    hostTypes: HostType[];
    saveHostTypes: (hostTypes: HostType[]) => Promise<void>;
}

const HostTypeConfiguration = (props: Props): JSX.Element => {
    const [ hostTypes, setHostTypes ] = useState<HostType[]>(props.hostTypes);
    const [ invalidExist, setInvalidExist ] = useState(false);

    const hostTypeColumns: TableProps.ColumnDefinition<HostType>[] = [
        {
            id: 'id',
            header: constants.HOST_NAME,
            cell: (e: HostType) =>
                <Input
                    value={e.id}
                    onChange={({ detail }) => updateHostType(e.id, 'id', detail.value)}
                    invalid={!e.id}
                />,
        }, {
            id: 'processors',
            header: constants.ON_PREM_PHYSICAL_PROCEESOR,
            cell: (e: HostType) => 
                <Input
                    type='number'
                    step={1}
                    value={e.processors.toString()}
                    onChange={({ detail }) => updateHostType(e.id, 'processors', detail.value)}
                    invalid={isInvalidNumericInput(e.processors)}/>,
        }, {
            id: 'cores',
            header: constants.ON_PREM_PHYSICAL_CORE,
            cell: (e: HostType) => 
                <Input
                    type='number'
                    step={1}
                    value={e.cores.toString()}
                    onChange={({ detail }) => updateHostType(e.id, 'cores', detail.value)}
                    invalid={isInvalidNumericInput(e.cores)}/>,
        }, {
            id: 'ram',
            header: constants.ON_PREM_PHYSICAL_RAM,
            cell: (e: HostType) => 
                <Input
                    type='number'
                    value={e.ram.toString()}
                    onChange={({ detail }) => updateHostType(e.id, 'ram', detail.value)}
                    invalid={isInvalidNumericInput(e.ram)}/>,
        }, {
            id: 'cpuSubsRatio',
            header: constants.ON_PREM_CPU_SUBSCRIPTION_RATIO,
            cell: (e: HostType) => 
                <Input
                    type='number'
                    step={0.1}
                    value={e.cpuSubsRatio.toString()}
                    onChange={({ detail }) => updateHostType(e.id, 'cpuSubsRatio', detail.value)}
                    invalid={isInvalidNumericInput(e.cpuSubsRatio)}/>,
        }, {
            id: 'hyperthread',
            header: constants.ON_PREM_HYPERTHREAD,
            cell: (e: HostType) => 
                <Input
                    type='number'
                    step={0.1}
                    value={e.hyperthread.toString()}
                    onChange={({ detail }) => updateHostType(e.id, 'hyperthread', detail.value)}
                    invalid={isInvalidNumericInput(e.hyperthread)}/>,
        }, {
            id: 'ramSubsRatio',
            header: constants.ON_PREM_RAM_SUBSCRIPTION_RATIO,
            cell: (e: HostType) =>
                <Input
                    type='number'
                    step={0.1}
                    value={e.ramSubsRatio.toString()}
                    onChange={({ detail }) => updateHostType(e.id, 'ramSubsRatio', detail.value)}
                    invalid={isInvalidNumericInput(e.ramSubsRatio)}/>,
        }, {
            id: 'removeBtn',
            header: '',
            cell: (e: HostType) =>
                <Button
                    iconName='close'
                    variant='normal'
                    onClick={() =>removeHostType(e.id)}
                    disabled={hostTypes.length <= 1}/>
        }
    ];

    const addHostType = (): void => {
        const deepCopiedHostTypes: HostType[] = deepCopy(hostTypes);
        const newHostType: HostType = {
            id: 'type' + (deepCopiedHostTypes.length + 1),
            cores: 0,
            cpuSubsRatio: 0,
            hyperthread: 0,
            processors: 0,
            ram: 0,
            ramSubsRatio: 0,
        };
        
        deepCopiedHostTypes.push(newHostType);
        setHostTypes(deepCopiedHostTypes);
        checkInput(deepCopiedHostTypes);
    };

    const removeHostType = (hostTypeId: string): void => {
        const deepCopiedHostTypes: HostType[] = deepCopy(hostTypes);
        const targetIndex = deepCopiedHostTypes.findIndex(hostType => hostType.id === hostTypeId);
        if (targetIndex < 0) {
            return;
        }
        deepCopiedHostTypes.splice(targetIndex, 1);
        setHostTypes(deepCopiedHostTypes);
        checkInput(deepCopiedHostTypes);
    };

    const isInvalidNumericInput = (value: number): boolean => {
        return isNaN(value) || value <= 0;
    };

    const isInvalidInput = (hostType: HostType): boolean => {
        let hostTypeProp: keyof typeof hostType;
        for (hostTypeProp in hostType) {
            if (!hostType[hostTypeProp]) {
                return true;
            }
            if (hostTypeProp !== 'id' && isInvalidNumericInput(hostType[hostTypeProp])) {
                return true;
            }
        }
        return false;
    };

    const checkInput = (hostTypes: HostType[]): void => {
        let inValid = false;
        hostTypes.forEach(hostType => {
            if (isInvalidInput(hostType)) {
                inValid = true;
                return;
            }
        });
        
        setInvalidExist(inValid);
    };

    const updateHostType = (id: string, column: keyof HostType, value: string): void => {
        const deepCopiedHostTypes: HostType[] = deepCopy(hostTypes);
        const targetIndex = deepCopiedHostTypes.findIndex(hostType => hostType.id === id);
        if (targetIndex < 0) {
            return;
        }
        const target = Object.assign(deepCopiedHostTypes[targetIndex], {});
        (target as any)[column] = column === 'id' ? value : parseFloat(value);
        deepCopiedHostTypes[targetIndex] = target;
        setHostTypes(deepCopiedHostTypes);
        checkInput(deepCopiedHostTypes);
    };

    const saveHostTypes = (): void => {
        props.saveHostTypes(hostTypes);
        props.setVisible(false);
    };

    return (
        <Modal
            size='max'
            visible={props.visible}
            onDismiss={() => props.setVisible(false)}
            header={constants.ON_PREM_CONFIGURE_HOST_TYPES}
            footer={
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='xs'>
                        <Button variant='link' onClick={() => props.setVisible(false)}>{constants.CANCEL_BUTTON}</Button>
                        <Button variant='primary' onClick={() => saveHostTypes()} disabled={invalidExist}>{constants.APPLY}</Button>
                    </SpaceBetween>
                </Box>
            }>
            <SpaceBetween size='l'>
                <Alert
                    visible={invalidExist}
                    type='error'
                    header={constants.INVALID_INPUT_FORMAT}
                >
                    {constants.INVALID_INPUT_FORMAT_DESCRIPTION}
                </Alert>
                <Table
                    items={hostTypes}
                    columnDefinitions={hostTypeColumns}
                    sortingDisabled
                />
                <Box float='left'>
                    <Button variant='primary' onClick={() => addHostType()}>{constants.ON_PREM_ADD_HOST_TYPE}</Button>
                </Box>
            </SpaceBetween>
        </Modal>
    );
};

export default HostTypeConfiguration;
